import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip, Modal, message as antdMessage } from 'antd';
import MarkdownRenderer from './MarkdownRenderer';
import StreamingCursor from './StreamingCursor';
import ThinkingIndicator from './ThinkingIndicator';
import GeneratedImage from '../images/GeneratedImage';
import ImagePlaceholder from '../images/ImagePlaceholder';
import ImageLightbox from '../files/ImageLightbox';
import AuthMedia from '../files/AuthMedia';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import { API_URL } from '../../../config';
import { formatUsdCost } from '../utils/money';

function resolveUrl(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
}

/** Компактные кнопки: иконка + название (как на скриншоте) */
const TextActionBtn = ({ label, tooltip, onClick, disabled, isDark, children }) => (
    <Tooltip title={tooltip} placement="top" mouseEnterDelay={0.25}>
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium shadow-sm transition disabled:opacity-40 ${
                isDark
                    ? 'border-white/15 bg-white/10 text-white/90 hover:bg-white/20'
                    : 'border-black/10 bg-white text-black/75 hover:bg-black/[0.04] hover:text-black'
            }`}
        >
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5">
                {children}
            </span>
            <span>{label}</span>
        </button>
    </Tooltip>
);

const IconCopy = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const IconRecreate = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M4 4v6h6M20 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 9A8 8 0 006.5 6.5L4 10M4 15a8 8 0 0013.5 2.5L20 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconReference = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3" y="5" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M17 9h2a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="10" cy="11" r="1.5" fill="currentColor" />
        <path d="M5 17l3.5-3.5L12 16l2-2 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconTelegram = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M21.5 3.3L2.8 10.5c-1.3.5-1.3 1.2-.2 1.5l4.8 1.5 1.9 5.8c.2.7.4.9 1 .9.5 0 .8-.2 1.1-.5l2.3-2.2 4.8 3.5c.9.5 1.5.2 1.7-.8l3.1-14.7c.3-1.3-.5-1.9-1.6-1.5zM9.2 14.1l-.3 3.5 1.5-2.1 7.6-6.8c.3-.2.6 0 .3.3l-8.1 7.1z" />
    </svg>
);

const IconDelete = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const MessageBubble = observer(({ message, imagePriority = 'auto' }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const isUser = message.role === 'user';
    const [lightbox, setLightbox] = useState(null);
    const [busy, setBusy] = useState(null);

    const isThinking =
        !isUser &&
        message.status === 'streaming' &&
        !(message.content || '').trim();

    const isGeneratingImage =
        !isUser &&
        (message.status === 'generating_image' ||
            ((message.pending_images || []).length > 0 &&
                !(message.generated_images || []).length));
    const isGeneratingVideo = !isUser && message.status === 'generating_video';
    const isGeneratingAudio = !isUser && message.status === 'generating_audio';

    const isAssistantText =
        !isUser &&
        !isThinking &&
        !isGeneratingImage &&
        message.status !== 'streaming' &&
        message.status !== 'generating_image' &&
        message.status !== 'generating_video' &&
        message.status !== 'generating_audio' &&
        !!(message.content || '').trim() &&
        !(message.generated_images || []).length;

    const isAssistantImages =
        !isUser &&
        !isThinking &&
        !isGeneratingImage &&
        message.status !== 'streaming' &&
        (message.generated_images || []).length > 0;
    const isAssistantMedia =
        !isUser &&
        !isThinking &&
        !isGeneratingImage &&
        !isGeneratingVideo &&
        !isGeneratingAudio &&
        message.status !== 'streaming' &&
        (message.attachments || []).some((a) => {
            const kind = `${a.kind || ''} ${a.mime || ''}`.toLowerCase();
            return kind.includes('audio') || kind.includes('video');
        });

    const canDelete =
        message.status !== 'streaming' &&
        message.status !== 'generating_image' &&
        message.status !== 'generating_video' &&
        message.status !== 'generating_audio' &&
        !isThinking &&
        !isGeneratingImage &&
        !isGeneratingVideo &&
        !isGeneratingAudio;

    const onDelete = () => {
        Modal.confirm({
            title: isUser ? 'Удалить сообщение?' : 'Удалить ответ?',
            content: isUser
                ? 'Будут удалены сообщение и следующий ответ ассистента (если есть).'
                : 'Ответ будет удалён из истории чата.',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: async () => {
                setBusy('delete');
                try {
                    await chatStore.deleteMessage(message.id);
                    antdMessage.success('Удалено');
                } catch (e) {
                    antdMessage.error(e.message || 'Не удалось удалить');
                } finally {
                    setBusy(null);
                }
            },
        });
    };

    const deleteBtn = canDelete ? (
        <TextActionBtn
            label="Delete"
            tooltip={isUser ? 'Удалить сообщение и ответ' : 'Удалить ответ'}
            onClick={onDelete}
            disabled={busy === 'delete'}
            isDark={isDark}
        >
            <IconDelete />
        </TextActionBtn>
    ) : null;

    const onCopyText = async () => {
        const text = (message.content || '').trim();
        if (!text) {
            antdMessage.info('Нечего копировать');
            return;
        }
        setBusy('copy');
        try {
            await navigator.clipboard.writeText(text);
            antdMessage.success('Скопировано');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось скопировать');
        } finally {
            setBusy(null);
        }
    };

    const onRecreateUser = async () => {
        setBusy('recreate');
        try {
            await chatStore.recreateUserMessage(message);
            antdMessage.success('Промпт и файлы подставлены');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось восстановить данные');
        } finally {
            setBusy(null);
        }
    };

    const onRecreateAssistant = async () => {
        setBusy('recreate');
        try {
            const idx = chatStore.messages.findIndex((m) => m.id === message.id);
            let prevUser = null;
            for (let i = idx - 1; i >= 0; i -= 1) {
                if (chatStore.messages[i].role === 'user') {
                    prevUser = chatStore.messages[i];
                    break;
                }
            }
            if (prevUser) {
                await chatStore.recreateUserMessage(prevUser);
                antdMessage.success('Промпт подставлен');
            } else {
                await chatStore.regenerate();
            }
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось повторить');
        } finally {
            setBusy(null);
        }
    };

    const onReferenceText = () => {
        const text = (message.content || '').trim();
        if (!text) {
            antdMessage.info('Нечего добавить');
            return;
        }
        const quote = text.length > 2000 ? `${text.slice(0, 2000)}…` : text;
        chatStore.setMode('chat');
        chatStore.setDraft(`«${quote}»\n\n`);
        antdMessage.success('Текст добавлен в промпт как референс');
    };

    const onTelegramText = async () => {
        const text = (message.content || '').trim();
        if (!text) {
            antdMessage.info('Нечего отправлять');
            return;
        }
        setBusy('telegram');
        try {
            if (typeof navigator.share === 'function') {
                try {
                    await navigator.share({ text });
                    return;
                } catch (e) {
                    if (e?.name === 'AbortError') return;
                }
            }
            window.open(
                `https://t.me/share/url?text=${encodeURIComponent(text)}`,
                '_blank',
                'noopener,noreferrer'
            );
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось открыть Telegram');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
                className={`flex w-full flex-col gap-2 ${
                    isUser ? 'items-end' : 'items-stretch'
                }`}
            >
                <div className="flex items-center gap-2 text-[11px] opacity-50">
                    <span>{isUser ? 'Вы' : 'Ассистент'}</span>
                </div>

                {(message.attachments || []).length > 0 && (
                    <div
                        className={`flex gap-2 ${
                            isUser ? 'w-full flex-col items-end' : 'flex-wrap'
                        }`}
                    >
                        {message.attachments.map((a) => {
                            const url = resolveUrl(a.url);
                            if ((a.kind || a.mime || '').includes('image')) {
                                return (
                                    <button
                                        key={a.file_id}
                                        type="button"
                                        onClick={() => setLightbox({ url: a.url, name: a.name })}
                                        className="bananas-img-preview overflow-hidden rounded-xl border border-black/10 dark:border-white/10"
                                    >
                                        <AuthMedia
                                            src={a.url}
                                            alt={a.name}
                                            priority={imagePriority}
                                            className="h-auto w-full object-contain"
                                        />
                                    </button>
                                );
                            }
                            if ((a.kind || a.mime || '').includes('audio')) {
                                const broken =
                                    (a.mime || '').includes('json') ||
                                    (Number(a.size) > 0 && Number(a.size) < 400);
                                return (
                                    <div
                                        key={a.file_id}
                                        className={`w-full max-w-md rounded-xl border px-3 py-2 ${
                                            isDark ? 'border-white/10 bg-white/5' : 'border-black/10 bg-white'
                                        }`}
                                    >
                                        {broken ? (
                                            <div className="text-xs text-red-500">
                                                Аудио не получено. Сгенерируйте ещё раз.
                                            </div>
                                        ) : (
                                            <AuthMedia src={a.url} as="audio" className="w-full" />
                                        )}
                                    </div>
                                );
                            }
                            if ((a.kind || a.mime || '').includes('video')) {
                                return (
                                    <div
                                        key={a.file_id}
                                        className="overflow-hidden rounded-xl border border-black/10 dark:border-white/10"
                                    >
                                        <AuthMedia src={a.url} as="video" className="w-full" />
                                    </div>
                                );
                            }
                            return (
                                <button
                                    key={a.file_id}
                                    type="button"
                                    onClick={async () => {
                                        const token = localStorage.getItem('token');
                                        const res = await fetch(url, {
                                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                                        });
                                        const blob = await res.blob();
                                        const obj = URL.createObjectURL(blob);
                                        window.open(obj, '_blank');
                                    }}
                                    className={`rounded-lg border px-3 py-2 text-xs ${
                                        isDark ? 'border-white/10 bg-white/5' : 'border-black/10 bg-white'
                                    }`}
                                >
                                    📎 {a.name}
                                </button>
                            );
                        })}
                    </div>
                )}

                {isThinking && <ThinkingIndicator />}

                {!isThinking &&
                    !isGeneratingImage &&
                    !isGeneratingVideo &&
                    !isGeneratingAudio &&
                    (isUser || message.content) && (
                    <div
                        className={`bananas-msg-font rounded-2xl px-4 py-3 ${
                            isUser
                                ? isDark
                                    ? 'bg-[#2f2f2f]'
                                    : 'bg-[#e8e8ea]'
                                : 'bg-transparent'
                        }`}
                    >
                        {isUser ? (
                            <div className="whitespace-pre-wrap">{message.content}</div>
                        ) : (
                            <div>
                                <MarkdownRenderer content={message.content || ''} />
                                {message.status === 'streaming' && <StreamingCursor />}
                            </div>
                        )}
                    </div>
                )}

                {isGeneratingAudio && (
                    <div
                        className={`flex min-h-[72px] w-full max-w-md items-center justify-center rounded-xl border text-sm ${
                            isDark
                                ? 'border-white/10 bg-white/5 text-white/60'
                                : 'border-black/10 bg-black/[0.04] text-black/50'
                        }`}
                    >
                        Генерация аудио…
                    </div>
                )}

                {isGeneratingVideo && (
                    <div
                        className={`flex aspect-video w-full items-center justify-center rounded-xl border text-sm ${
                            isDark
                                ? 'border-white/10 bg-white/5 text-white/60'
                                : 'border-black/10 bg-black/[0.04] text-black/50'
                        }`}
                    >
                        Генерация видео…
                    </div>
                )}

                {isGeneratingImage && (
                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                        {(message.pending_images || [{ aspect_ratio: message.aspect_ratio || '1:1' }]).map(
                            (ph) => (
                                <ImagePlaceholder
                                    key={ph.key || ph.aspect_ratio}
                                    aspectRatio={ph.aspect_ratio || message.aspect_ratio || '1:1'}
                                />
                            )
                        )}
                    </div>
                )}

                {(message.generated_images || []).length > 0 && (
                    <div className="flex w-full flex-col gap-3">
                        {message.generated_images.map((img) => (
                            <GeneratedImage
                                key={img.file_id || img.url}
                                image={img}
                                model={message.model}
                                message={message}
                                priority={imagePriority}
                            />
                        ))}
                    </div>
                )}

                {isUser && (message.content || (message.attachments || []).length > 0) && (
                    <div className="bananas-btn-scale -mt-0.5 flex flex-wrap items-center gap-1.5">
                        <TextActionBtn
                            label="Copy"
                            tooltip="Скопировать текст сообщения"
                            onClick={onCopyText}
                            disabled={busy === 'copy' || !(message.content || '').trim()}
                            isDark={isDark}
                        >
                            <IconCopy />
                        </TextActionBtn>
                        <TextActionBtn
                            label="Recreate"
                            tooltip="Подставить текст и файлы в промпт"
                            onClick={onRecreateUser}
                            disabled={busy === 'recreate'}
                            isDark={isDark}
                        >
                            <IconRecreate />
                        </TextActionBtn>
                        {deleteBtn}
                    </div>
                )}

                {isAssistantText && (
                    <div className="bananas-btn-scale -mt-0.5 flex flex-wrap items-center gap-1.5">
                        <TextActionBtn
                            label="Copy"
                            tooltip="Скопировать текст ответа"
                            onClick={onCopyText}
                            disabled={busy === 'copy'}
                            isDark={isDark}
                        >
                            <IconCopy />
                        </TextActionBtn>
                        <TextActionBtn
                            label="Recreate"
                            tooltip="Подставить исходный промпт для повторной генерации"
                            onClick={onRecreateAssistant}
                            disabled={busy === 'recreate'}
                            isDark={isDark}
                        >
                            <IconRecreate />
                        </TextActionBtn>
                        <TextActionBtn
                            label="Reference"
                            tooltip="Добавить этот ответ в промпт как референс"
                            onClick={onReferenceText}
                            isDark={isDark}
                        >
                            <IconReference />
                        </TextActionBtn>
                        <TextActionBtn
                            label="Telegram"
                            tooltip="Отправить текст в Telegram"
                            onClick={onTelegramText}
                            disabled={busy === 'telegram'}
                            isDark={isDark}
                        >
                            <IconTelegram />
                        </TextActionBtn>
                        {deleteBtn}
                    </div>
                )}

                {(isAssistantImages || isAssistantMedia) && deleteBtn ? (
                    <div className="bananas-btn-scale -mt-0.5 flex flex-wrap items-center gap-1.5">
                        {deleteBtn}
                    </div>
                ) : null}

                {!isUser &&
                    message.status !== 'streaming' &&
                    message.status !== 'generating_image' &&
                    message.status !== 'generating_video' &&
                    message.status !== 'generating_audio' && (
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] opacity-55">
                        {message.model ? <span>{message.model}</span> : null}
                        {message.usage?.total_tokens != null ? (
                            <span>
                                {message.model ? '· ' : null}
                                токены: {message.usage.total_tokens}
                            </span>
                        ) : null}
                        {(() => {
                            const cost =
                                message.usage?.cost != null
                                    ? message.usage.cost
                                    : (message.generated_images || []).reduce(
                                          (sum, img) => sum + (Number(img.cost) || 0),
                                          0
                                      );
                            if (cost == null || Number.isNaN(Number(cost))) return null;
                            if (message.usage?.cost == null && !(message.generated_images || []).length) {
                                return null;
                            }
                            if (message.usage?.cost == null && cost === 0) return null;
                            return (
                                <span>
                                    · {formatUsdCost(cost, chatStore.settings)}
                                </span>
                            );
                        })()}
                        {message.status === 'error' && (
                            <span className="text-red-500">
                                {message.error?.message || 'Ошибка'}{' '}
                                <button type="button" className="underline" onClick={() => chatStore.regenerate()}>
                                    Повторить
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>
            {lightbox && (
                <ImageLightbox
                    url={lightbox.url}
                    name={lightbox.name}
                    onClose={() => setLightbox(null)}
                />
            )}
        </div>
    );
});

export default MessageBubble;
